import { z } from "zod";
/**
 * Ideation Knowledge Maps — the persisted, AI-coupled model.
 *
 * Distinct from `ideation.ts` (the read-only graph PROJECTION over notes):
 * this is the authored, persisted model behind the maps-of-maps system where
 * each node is a context store and the graph is Claude's structured memory.
 * Concept + data model: docs/enhancements/ideation-knowledge-maps-2026-06.md.
 *
 * `kind` and `rel` are stored as loose strings (not closed enums) so an
 * AI-extracted node/edge with a novel kind never fails validation; the UI keys
 * colour/affordances off the KNOWN_* vocabularies below and falls back
 * gracefully for anything else. (Loose-response / extensible-vocabulary
 * principle.)
 */
// ─── Controlled-but-extensible vocabularies ────────────────────────────────
export const KNOWN_NODE_KINDS = [
    "device",
    "component",
    "brief",
    "patent",
    "claim",
    "methodology",
    "concept",
    "open-question",
    "standard",
    "party",
];
export const KNOWN_REL_TYPES = [
    "builds-on",
    "integrates",
    "part-of",
    "depends-on",
    "implements",
    "references",
    "supersedes",
    "patent-claims",
    "classifies",
    "meters",
    "questions",
    "addresses",
    "alternative-to",
    "excludes",
];
/**
 * Canonical Subject ↔ open-question edge for Capture.
 *
 * Existing graph convention: `questions` is read as "source questions target".
 * Capture therefore always writes:
 *   from = the open-question node, to = the Subject, rel = `"questions"`.
 *
 * `addresses` is the inverse narrative (a Subject answering or closing a
 * question). Capture must not pick it for a new unanswered question.
 */
export const SUBJECT_OPEN_QUESTION_EDGE = {
    fromKind: "open-question",
    rel: "questions",
};
export function subjectOpenQuestionEdgeCreate(input) {
    return {
        from: input.questionNodeId,
        to: input.subjectId,
        rel: SUBJECT_OPEN_QUESTION_EDGE.rel,
        note: null,
        provenance: [{ kind: "manual", ref: "staff-capture", span: null }],
        weight: null,
    };
}
/** Per-map membership canvas display (journey declutter — not ADR-0109 node currency). */
export const MemberDisplaySchema = z.enum(["shown", "superseded"]);
/**
 * @deprecated Superseded by the per-tenant map-kind taxonomy + `MAP_BEHAVIORS`
 * (ideation-taxonomy.ts, ADR-0066). A map's `kind` is now a loose string slug
 * resolved against the tenant taxonomy; the closed enum lives on `behavior`.
 * Retained only until all consumers migrate off it.
 */
export const MAP_KINDS = ["device", "lens", "hub"];
export const MapKindSchema = z.enum(MAP_KINDS);
export const SOURCE_REF_KINDS = ["document", "transcript", "note", "manual"];
// ─── Provenance ────────────────────────────────────────────────────────────
export const SourceRefSchema = z.object({
    kind: z.enum(SOURCE_REF_KINDS),
    /** Document id, transcript/session id, note id, or a free label for manual. */
    ref: z.string().min(1).max(400),
    /** Optional locator within the source (section, line range, message id). */
    span: z.string().max(400).nullable().default(null),
});
// ─── Node context payload (the heart) ──────────────────────────────────────
export const NodeDecisionSchema = z.object({
    text: z.string().min(1).max(4000),
    at: z.string().datetime(),
    by: z.string().max(320),
    detail: z.string().max(20_000).optional(),
    documentIds: z.array(z.string().uuid()).max(10).default([]),
    conversationId: z.string().uuid().optional(),
    messageId: z.string().uuid().optional(),
});
export const NodeChatExcerptSchema = z.object({
    sessionId: z.string().min(1),
    quote: z.string().min(1).max(8000),
});
// ─── Clippings (Phase 2: typed captured snippets) ───────────────────────────
/**
 * A captured snippet attached to a node's context. `url` is null for
 * paste-style captures (no origin URL). `html` preserves rich formatting when
 * available; `text` is the plain-text fallback and the search/extraction target.
 * Stored as `clippings[]` on the node context — a dedicated first-class array,
 * NOT a sourceRef kind.
 */
export const ClippingSchema = z.object({
    id: z.string().uuid(),
    url: z.string().url().nullable().default(null),
    text: z.string().max(8000),
    html: z.string().max(16000).nullable().default(null),
    capturedAt: z.string().datetime(),
    capturedBy: z.string().max(320),
});
// ─── Clipping URL-fetch endpoint (Phase 2: server-side SSRF-guarded fetch) ──
/**
 * Request/response for `POST /ideation-maps/clippings/fetch`.
 *
 * The browser can't fetch cross-origin pages itself; this endpoint delegates
 * to `unfurlLink` which carries the full SSRF-guard posture (host blocklist,
 * redirect refusal, 2 MB/5 s caps — ADR-0017).
 *
 * v1 text content = the page's og:description snippet (full-page extraction
 * is deferred); `title` mirrors the unfurl title field so callers don't need
 * a separate lookup. The paste path (user copies text) goes through a normal
 * node PATCH and does not need this endpoint.
 */
export const ClippingFetchRequestSchema = z.object({ url: z.string().url() });
export const ClippingFetchResponseSchema = z.object({
    url: z.string(),
    title: z.string().nullable(),
    text: z.string(),
});
// ─── Structured facts (ADR-0109: deterministic write path) ──────────────────
/**
 * One typed, verbatim fact on a node — a part number, a unit cost, a country of
 * origin, a thermal grade, a rule number.
 *
 * **Why this exists separately from `body`:** everything else in the node
 * context is prose that the extraction pipeline *infers* structure from. For a
 * part number or a cost, inference is not acceptable — the value must be stored
 * exactly as given. Facts are written only by the deterministic structured
 * path, never by extraction.
 *
 * `source` + `date` are REQUIRED, so provenance is a field rather than a
 * convention: a fact you cannot attribute cannot be written.
 */
export const NodeFactSchema = z.object({
    /** Stable slug within the node, e.g. `part_number`, `unit_cost`, `origin`. */
    key: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9_]*$/, "key must be lower_snake_case"),
    /** Stored verbatim — never normalised, rounded, or re-derived. */
    value: z.string().min(1).max(400),
    /** Unit or currency where the value alone is ambiguous ("USD", "°C"). */
    unit: z.string().max(40).nullable().default(null),
    /** Where the value came from: document name, brief section, person, ticket. */
    source: z.string().min(1).max(400),
    /** `YYYY-MM-DD` or a full ISO-8601 timestamp — when the value was true/agreed. */
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp"),
    note: z.string().max(1_000).nullable().default(null),
    /** Audit trail of the write itself (who/when), distinct from `source`/`asOf`. */
    recordedBy: z.string().max(320).default(""),
    recordedAt: z.string().default(""),
});
/**
 * Supersede marker (ADR-0109 / G1: additive, never destructive).
 *
 * A superseded node keeps its body and its edges — nothing is deleted — but
 * every read path that surfaces the node MUST lead with this marker, so a stale
 * answer can never be returned as though it were current. `byNodeId` is the
 * replacement node when one exists; `null` means "retired, no successor".
 */
export const NodeSupersededSchema = z.object({
    byNodeId: z.string().min(1).max(100).nullable().default(null),
    byLabel: z.string().max(300).nullable().default(null),
    reason: z.string().min(1).max(2_000),
    at: z.string().min(4).max(40),
    by: z.string().max(320).default(""),
});
const ASOF_RE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/;
/** Staff POST /nodes/:id/supersede body (nodeId is the URL param). */
export const IdeationNodeSupersedeBodySchema = z.object({
    supersededByNodeId: z.string().min(1).max(100).nullable().default(null),
    reason: z.string().min(1).max(2_000),
    asOf: z.string().regex(ASOF_RE, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp"),
    dryRun: z.boolean().default(false),
});
export const IdeationNodeContextSchema = z.object({
    /** 1–2 lines used when this node is a *neighbour* in another node's context-assembly. */
    summary: z.string().max(600).default(""),
    /** The accumulated context for this node (markdown). */
    body: z.string().max(100_000).default(""),
    sourceRefs: z.array(SourceRefSchema).default([]),
    decisions: z.array(NodeDecisionSchema).default([]),
    chatExcerpts: z.array(NodeChatExcerptSchema).default([]),
    clippings: z.array(ClippingSchema).max(200).default([]),
    /** Typed, verbatim, individually-sourced facts (ADR-0109). Keys are unique. */
    facts: z.array(NodeFactSchema).max(200).default([]),
    /** Set when this node has been superseded; `null` = current. */
    superseded: NodeSupersededSchema.nullable().default(null),
});
// ─── Node ──────────────────────────────────────────────────────────────────
export const IdeationMapNodeSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1).max(300),
    /** Loose: a KNOWN_NODE_KINDS value or any AI-proposed kind. */
    kind: z.string().min(1).max(60),
    aliases: z.array(z.string().min(1).max(300)).default([]),
    context: IdeationNodeContextSchema.default({}),
    status: z.enum(["active", "archived"]).default("active"),
    provenance: z.array(SourceRefSchema).default([]),
    /**
     * Manual importance override (drives node size in the renderer). `null` =
     * auto, where the UI derives weight from the node's edge degree
     * (connectedness). Set a value to pin a node's prominence regardless.
     */
    weight: z.number().int().min(0).max(20).nullable().default(null),
    /** Phase 2 node nesting: parent node id within the same map. null = top-level. Loose (response) — a plain string id. */
    parentNodeId: z.string().nullable().default(null),
    /** Per-map canvas display within a map view (default shown; superseded hidden unless includeJourney). */
    display: MemberDisplaySchema.default("shown"),
});
export const SupersedeNodeResultSchema = z.object({
    applied: z.boolean(),
    nodeId: z.string(),
    label: z.string(),
    supersededBy: z.object({ id: z.string(), label: z.string() }).nullable(),
    marker: NodeSupersededSchema,
    edgeCreated: z.boolean(),
    node: IdeationMapNodeSchema.nullable(),
    warnings: z.array(z.string()),
});
// ─── Edge (typed relationship) ──────────────────────────────────────────────
export const IdeationMapEdgeSchema = z.object({
    id: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
    /** Loose: a KNOWN_REL_TYPES value or any AI-proposed relationship. */
    rel: z.string().min(1).max(60),
    note: z.string().max(2000).nullable().default(null),
    provenance: z.array(SourceRefSchema).default([]),
    /**
     * Relationship strength — drives edge thickness in the renderer. `null` =
     * default weight (renders as 1). The UI sets 0–5 (see the CREATE schema's
     * `.max(5)`), but this RESPONSE field stays loose (int, nullable, no max) per
     * the loosen-output convention: an edge persisted under the old `max(20)`
     * range must still parse, or the whole map view would break on one stray row.
     */
    weight: z.number().int().nullable().default(null),
    /** Soft-archive timestamp; null = active. Loose (response). */
    archivedAt: z.string().datetime().nullable().default(null),
});
// ─── Map (device | lens | hub) ──────────────────────────────────────────────
export const NodePositionSchema = z.object({ x: z.number(), y: z.number() });
export const IdeationMapSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1).max(200),
    kind: z.string().min(1).max(60),
    /**
     * Short Project instructions (Claude Projects analogue). Injected into
     * map-scoped chat framing and Ask Insights when this map is pinned.
     * Empty string = none. See Projects-as-maps design (2026-08-02).
     */
    instructions: z.string().max(4000).default(""),
    /** Entity (node) ids for device/lens maps — one identity, may appear in many maps. */
    members: z.array(z.string().min(1)).default([]),
    /** Child map ids for hub maps (drill-down), e.g. the index → Devices group. */
    childMaps: z.array(z.string().min(1)).default([]),
    /** Lens-local curated edges — the narrative a structural graph can't express. */
    flowEdges: z.array(IdeationMapEdgeSchema).default([]),
    /** Optional left→right sequence for flow lenses (entityId → index). */
    order: z.record(z.string(), z.number()).nullable().default(null),
    /** Saved positions per map so curated maps keep their layout; auto maps re-flow. */
    layout: z.record(z.string(), NodePositionSchema).default({}),
    membership: z.enum(["auto", "curated"]).default("curated"),
    /** For auto maps: how membership is derived (e.g. "kind=open-question"). */
    membershipRule: z.string().max(400).nullable().default(null),
    /** tenant = everyone with the ideation role; private = owner + sharedWith. */
    visibility: z.enum(["tenant", "private"]).default("tenant"),
    /** Emails that may see a private map (ignored when visibility = tenant). Loose (response). */
    sharedWith: z.array(z.string()).default([]),
    /** True when the requesting user owns this map (drives edit affordances).
     *  Server-populated per request; not a persisted column. Fails closed (false). */
    isOwner: z.boolean().default(false),
});
// ─── Write DTOs (tighter than the stored shape) ─────────────────────────────
export const IdeationMapNodeCreateSchema = z.object({
    label: z.string().min(1).max(300),
    kind: z.string().min(1).max(60),
    aliases: z.array(z.string().min(1).max(300)).default([]),
    context: IdeationNodeContextSchema.partial().default({}),
    provenance: z.array(SourceRefSchema).default([]),
    weight: z.number().int().min(0).max(20).nullable().default(null),
});
export const IdeationMapNodeUpdateSchema = IdeationMapNodeCreateSchema.partial()
    .extend({ status: z.enum(["active", "archived"]).optional() })
    .refine((v) => Object.keys(v).length > 0, { message: "No fields to update" });
/** Generic HTTP node writes must not replace canonical facts[]. */
export const CANONICAL_FACTS_HTTP_MESSAGE = "Canonical facts cannot be written through generic node create/update. Use staff Capture or MCP upsert_node.";
function rejectHttpCanonicalFacts(data, ctx) {
    if (!data || typeof data !== "object")
        return;
    const context = data.context;
    if (context && typeof context === "object" && context !== null && "facts" in context) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CANONICAL_FACTS_HTTP_MESSAGE,
            path: ["context", "facts"],
        });
    }
}
export const IdeationMapNodeHttpCreateSchema = z
    .unknown()
    .superRefine(rejectHttpCanonicalFacts)
    .pipe(IdeationMapNodeCreateSchema);
export const IdeationMapNodeHttpUpdateSchema = z
    .unknown()
    .superRefine(rejectHttpCanonicalFacts)
    .pipe(IdeationMapNodeUpdateSchema);
/** Request body for PATCH /nodes/:id/parent. `parentNodeId: null` = un-nest to top level. */
export const NodeSetParentSchema = z.object({
    mapId: z.string().min(1),
    parentNodeId: z.string().min(1).nullable().default(null),
});
export const IdeationMapEdgeCreateSchema = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
    rel: z.string().min(1).max(60),
    note: z.string().max(2000).nullable().default(null),
    provenance: z.array(SourceRefSchema).default([]),
    /** Relationship strength (0–5). `null` = default (renders as 1). */
    weight: z.number().int().min(0).max(5).nullable().default(null),
});
export const IdeationMapCreateSchema = z.object({
    title: z.string().min(1).max(200),
    kind: z.string().min(1).max(60),
    /** Optional Project instructions (max 4k). Defaults to empty. */
    instructions: z.string().max(4000).default(""),
    members: z.array(z.string().min(1)).default([]),
    childMaps: z.array(z.string().min(1)).default([]),
    membership: z.enum(["auto", "curated"]).default("curated"),
    membershipRule: z.string().max(400).nullable().default(null),
    visibility: z.enum(["tenant", "private"]).default("tenant"),
    sharedWith: z.array(z.string().email()).max(200).default([]),
});
export const IdeationMapUpdateSchema = IdeationMapCreateSchema.partial()
    .extend({
    flowEdges: z.array(IdeationMapEdgeSchema).optional(),
    order: z.record(z.string(), z.number()).nullable().optional(),
    layout: z.record(z.string(), NodePositionSchema).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "No fields to update" });
// ─── Read responses (loose) ─────────────────────────────────────────────────
export const IdeationMapViewSchema = z.object({
    map: IdeationMapSchema,
    nodes: z.array(IdeationMapNodeSchema),
    edges: z.array(IdeationMapEdgeSchema),
    /** Map ids this map's members also appear in — the cross-map "doorways". */
    alsoIn: z.record(z.string(), z.array(z.string())).default({}),
});
export const IdeationMapListResponseSchema = z.object({
    maps: z.array(IdeationMapSchema),
});
export const IdeationMapNodeListResponseSchema = z.object({
    nodes: z.array(IdeationMapNodeSchema),
});
/** Add/remove a node's membership of a map (the `member` flag). */
export const IdeationMapMemberSetSchema = z.object({
    nodeId: z.string().min(1),
    member: z.boolean().default(true),
});
/** PATCH membership canvas display (mark superseded / restore to canvas). */
export const SetMemberDisplaySchema = z.object({
    mapId: z.string().min(1),
    nodeId: z.string().min(1),
    display: MemberDisplaySchema,
});
/** GET map view query — `includeJourney` reveals superseded members on the canvas. */
export const MapViewQuerySchema = z.object({
    includeJourney: z.coerce.boolean().optional().default(false),
});
/** Merge one node into another (identity dedup): `:id` is kept, `dropId` is absorbed. */
export const IdeationMapNodeMergeSchema = z.object({
    dropId: z.string().min(1),
});
// ─── Phase 2: context-assembly + grounded chat (the read loop) ──────────────
//
// When the user opens a node (or map) to chat, the server assembles a
// token-budgeted, *source-cited* context bundle and grounds the L30 Claude
// provider on it. The transcript is persisted so it can later feed the
// Phase-3 extraction (write) loop. Concept:
// docs/enhancements/ideation-knowledge-maps-2026-06.md §"The Claude loop".
export const IDEATION_CHAT_SCOPES = ["node", "map", "corpus"];
export const IdeationChatScopeSchema = z.enum(IDEATION_CHAT_SCOPES);
/**
 * One cited node in an assembled context bundle. The UI renders these as
 * source chips so every grounded answer is traceable back to a node.
 */
export const IdeationContextSourceSchema = z.object({
    nodeId: z.string().min(1),
    label: z.string().min(1).max(300),
    /** Loose — a KNOWN_NODE_KINDS value or any AI-proposed kind. */
    kind: z.string().min(1).max(60),
    /** How this source relates to the focus: "focus", a rel type, or "member". */
    relation: z.string().min(1).max(60),
    /** The excerpt placed in the bundle (already budget-trimmed). */
    excerpt: z.string().max(8000).default(""),
});
/**
 * The token-budgeted bundle the server assembles. Returned both as a preview
 * (GET) so the UI can show "what Claude will see", and inside a chat answer.
 */
export const IdeationAssembledContextSchema = z.object({
    scope: IdeationChatScopeSchema,
    scopeId: z.string().min(1),
    /** Human framing — node label or map title. */
    title: z.string().max(300).default(""),
    /** The assembled prompt context (markdown), already budget-trimmed. */
    text: z.string().default(""),
    sources: z.array(IdeationContextSourceSchema).default([]),
    /** Rough token estimate of `text` (chars/4) so the UI can warn near budget. */
    tokenEstimate: z.number().int().nonnegative().default(0),
    /** True when assembly dropped neighbours/excerpts to fit the budget. */
    truncated: z.boolean().default(false),
});
// Response shapes are loose (system-generated timestamps, accumulated history).
export const IdeationChatMessageSchema = z.object({
    id: z.string().min(1),
    sessionId: z.string().min(1),
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    /** Sources the assistant grounded on (empty for user turns). */
    sources: z.array(IdeationContextSourceSchema).default([]),
    createdAt: z.string(),
    /**
     * Phase 4 (edit/regenerate as branches): the id of the message this turn
     * hangs from. Null for root messages (the first user turn in a linear session).
     * Loose: nullable so pre-Phase-4 rows (which have no parent) parse cleanly.
     */
    parentMessageId: z.string().uuid().nullable().default(null),
});
export const IdeationChatSessionSchema = z.object({
    id: z.string().min(1),
    scope: IdeationChatScopeSchema,
    scopeId: z.string().min(1),
    title: z.string().max(300).default(""),
    createdAt: z.string(),
    updatedAt: z.string(),
    messageCount: z.number().int().nonnegative().default(0),
});
// ─── Chat DTOs (request tight, response loose) ──────────────────────────────
export const IdeationChatAskSchema = z.object({
    scope: IdeationChatScopeSchema,
    scopeId: z.string().min(1),
    question: z.string().min(1).max(8000),
    /** Continue an existing session, or omit/null to start a new one. */
    sessionId: z.string().min(1).nullable().default(null),
    /**
     * REGENERATE path (Phase 4): attach the new assistant turn directly under
     * this existing message id without inserting a new user row.  The cited
     * message must be a user turn in the same session.  Set to null for normal
     * ask and edit turns.
     */
    parentMessageId: z.string().min(1).nullable().default(null),
    /**
     * EDIT path (Phase 4): insert a new user turn whose parent is this message
     * id, then attach the assistant under the new user turn.  This makes the
     * edited text a sibling of the original user question rather than appending
     * at the tip.
     *
     * When this field is PRESENT (even when null — which means the original user
     * turn is a root message), the service takes the EDIT path and parents the
     * new user turn on exactly this value.  When ABSENT (undefined), the service
     * takes the NORMAL path and parents on the session's current active leaf.
     *
     * The `optional()` (no default) is intentional: it lets the service
     * distinguish "caller sent null (root-edit)" from "caller omitted (normal)".
     */
    userParentMessageId: z.string().min(1).nullable().optional(),
    /** Neighbour hops for node-scope assembly (0 = focus node only). */
    depth: z.number().int().min(0).max(2).default(1),
});
export const IdeationChatAnswerSchema = z.object({
    sessionId: z.string().min(1),
    generatedAt: z.string(),
    userMessage: IdeationChatMessageSchema,
    assistantMessage: IdeationChatMessageSchema,
    /** The context bundle used, so the UI can show what Claude saw. */
    context: IdeationAssembledContextSchema,
});
/**
 * PATCH `/chat/sessions/:id` — set the active leaf (branch tip) for a session.
 * Used by the UI after a regenerate or when the user navigates between branches.
 * Tight request schema: activeLeafId must be a valid UUID.
 */
export const IdeationChatSessionPatchSchema = z.object({
    activeLeafId: z.string().uuid(),
});
/**
 * Query chat sessions.
 * - Scoped: both `scope` + `scopeId` → sessions for that grounding.
 * - Library: both omitted → cross-scope “mine” list (conversations home).
 * Mixing one without the other is rejected.
 */
export const IdeationChatSessionQuerySchema = z
    .object({
    scope: IdeationChatScopeSchema.optional(),
    scopeId: z.string().min(1).optional(),
})
    .superRefine((value, ctx) => {
    const hasScope = value.scope !== undefined;
    const hasScopeId = value.scopeId !== undefined;
    if (hasScope !== hasScopeId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "scope and scopeId must both be provided, or both omitted for the library list",
            path: hasScope ? ["scopeId"] : ["scope"],
        });
    }
});
export const IdeationChatSessionListResponseSchema = z.object({
    sessions: z.array(IdeationChatSessionSchema),
});
export const IdeationChatMessageListResponseSchema = z.object({
    session: IdeationChatSessionSchema,
    messages: z.array(IdeationChatMessageSchema),
    /**
     * Phase-4 branch-point: the message id at the tip of the currently active
     * conversation branch (edit / regenerate). Omitted by the current backend
     * (pre-Phase 4) — `.default(null)` means the current response still parses
     * to null so forward-compat works without a cast at the call-site.
     */
    activeLeafId: z.string().min(1).nullable().default(null),
});
// ─── Phase 3: extraction → reviewed diff → merge (the write loop) ───────────
//
// A document, an in-app chat transcript, or an imported external transcript is
// run through an extraction agent that PROPOSES new nodes, typed edges, and
// context additions to existing nodes. Output is never auto-merged: it is a
// reviewed diff (accept / edit / reject per item). Proposed nodes are
// dedup-matched against existing identities so the graph never forks a
// duplicate. Concept: docs/enhancements/ideation-knowledge-maps-2026-06.md
// §"Extraction (write direction)"; ADR-0063.
export const EXTRACTION_SOURCE_KINDS = ["chat", "text", "document"];
export const ExtractionSourceKindSchema = z.enum(EXTRACTION_SOURCE_KINDS);
export const EXTRACTION_ITEM_TYPES = ["node", "edge", "context"];
export const ExtractionItemTypeSchema = z.enum(EXTRACTION_ITEM_TYPES);
export const EXTRACTION_ITEM_STATUSES = ["pending", "accepted", "rejected", "applied"];
export const ExtractionItemStatusSchema = z.enum(EXTRACTION_ITEM_STATUSES);
/** Match confidence of a proposed node against the existing graph (dedup). */
export const EXTRACTION_MATCH_KINDS = ["label", "alias", "embedding", "none"];
export const ExtractionMatchKindSchema = z.enum(EXTRACTION_MATCH_KINDS);
/**
 * Agent self-rated confidence in a proposal, 0..1. Drives hybrid-curation
 * banding (ADR-0075) together with `matchKind`. Loose on read (an older agent or
 * a parse miss yields `null`); `null` is treated as the lowest band by the
 * classifier, so absence never auto-applies.
 */
export const ProposalConfidenceSchema = z.number().min(0).max(1).nullable().default(null);
// ── The agent's raw proposal (AI output / deterministic output) ──
/** A proposed new node. `tempId` lets proposed edges reference it pre-persist. */
export const ExtractionNodeProposalSchema = z.object({
    tempId: z.string().min(1).max(60),
    label: z.string().min(1).max(300),
    kind: z.string().min(1).max(60),
    summary: z.string().max(600).default(""),
    body: z.string().max(100_000).default(""),
    confidence: ProposalConfidenceSchema,
});
/** A proposed edge. `from`/`to` reference a proposal `tempId` OR an existing node label. */
export const ExtractionEdgeProposalSchema = z.object({
    from: z.string().min(1).max(300),
    to: z.string().min(1).max(300),
    rel: z.string().min(1).max(60),
    note: z.string().max(2000).nullable().default(null),
    confidence: ProposalConfidenceSchema,
});
/** Context to append to an existing node, identified by label. */
export const ExtractionContextProposalSchema = z.object({
    nodeLabel: z.string().min(1).max(300),
    text: z.string().min(1).max(8000),
});
export const ExtractionProposalSchema = z.object({
    nodes: z.array(ExtractionNodeProposalSchema).default([]),
    edges: z.array(ExtractionEdgeProposalSchema).default([]),
    contextAdditions: z.array(ExtractionContextProposalSchema).default([]),
});
/**
 * Hybrid-curation band for one proposed item (ADR-0075):
 *  - `auto`  — safe additive item; policy may accept+apply it autonomously.
 *  - `queue` — uncertain (soft match / possible duplicate / merge); human reviews.
 *  - `flag`  — contradiction / version supersession; never auto-applied.
 */
export const CURATION_BANDS = ["auto", "queue", "flag"];
export const CurationBandSchema = z.enum(CURATION_BANDS);
/**
 * Per-map (fallback per-tenant) hybrid-curation policy — the dial from
 * full-manual to near-autonomous (ADR-0075). The conservative default
 * ({@link DEFAULT_CURATION_POLICY}) is `enabled:false` ⇒ every item queues for
 * human review, i.e. today's behaviour. Autonomy is strictly opt-in.
 */
export const CurationPolicySchema = z.object({
    enabled: z.boolean().default(false),
    /** Min agent confidence for an additive item to qualify for the `auto` band. */
    autoConfidence: z.number().min(0).max(1).default(0.75),
    /** Item types eligible for auto-accept; types omitted here always queue. */
    autoItemTypes: z.array(ExtractionItemTypeSchema).default(["node", "edge", "context"]),
    /**
     * Allow `matchKind:"embedding"` node items to auto-MERGE into their matched
     * identity (ADR-0092). Strictly opt-in and separate from `enabled` — a map
     * can auto-accept novel items while still queueing every merge. Keep false
     * until merge recall has been measured on real embeddings for this corpus
     * (`packages/api/src/scripts/measure-dedupe-recall.ts`).
     */
    autoMerge: z.boolean().default(false),
    /** Min embedding similarity (0..1 cosine) for an auto-merge; below ⇒ queue. */
    autoMergeSimilarity: z.number().min(0).max(1).default(0.95),
});
/** Safe default: autonomy off — identical to pre-ADR-0075 full-manual review. */
export const DEFAULT_CURATION_POLICY = {
    enabled: false,
    autoConfidence: 0.75,
    autoItemTypes: ["node", "edge", "context"],
    autoMerge: false,
    autoMergeSimilarity: 0.95,
};
// ── The persisted, reviewable diff ──
export const ExtractionItemSchema = z.object({
    id: z.string().min(1),
    itemType: ExtractionItemTypeSchema,
    status: ExtractionItemStatusSchema,
    /** The proposed node/edge/context payload (shape depends on itemType). */
    payload: z.record(z.string(), z.unknown()),
    /** For node/context items: the existing node this was dedup-matched to. */
    matchNodeId: z.string().nullable().default(null),
    matchKind: ExtractionMatchKindSchema.default("none"),
    /** Agent self-rated confidence (0..1), carried from the proposal; null if unrated. */
    confidence: z.number().min(0).max(1).nullable().default(null),
    /** Hybrid-curation band assigned at build time; null for pre-ADR-0075 rows. */
    band: CurationBandSchema.nullable().default(null),
    /** What the apply step created/updated (null until applied). */
    resultNodeId: z.string().nullable().default(null),
    resultEdgeId: z.string().nullable().default(null),
});
/**
 * Extraction run state. Async (the AI call runs in the background, off the
 * request path, so it can't hit the gateway timeout):
 *   `processing` → the agent is running (no items yet; poll until it changes)
 *   `pending`    → proposed, awaiting per-item review (items present)
 *   `failed`     → the agent errored (see API logs; retry)
 *   `applied` / `discarded` → terminal review outcomes
 * Loose on read so an unknown/legacy status never breaks the poll render.
 */
export const ExtractionViewSchema = z.object({
    id: z.string().min(1),
    sourceKind: ExtractionSourceKindSchema,
    sourceRef: z.string().max(400).default(""),
    status: z.string().min(1).max(20),
    /** Target map for apply / Pending inbox; null when capture was mapless. */
    mapId: z.string().min(1).nullable().default(null),
    items: z.array(ExtractionItemSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Lightweight open-queue summary for nav badges (caller-scoped). */
export const OpenExtractionsSummarySchema = z.object({
    count: z.number().int().nonnegative(),
    byMap: z.array(z.object({
        mapId: z.string().min(1).nullable(),
        count: z.number().int().positive(),
    })),
});
// ── DTOs ──
/** Run an extraction. Exactly one of sessionId / text / rawSource is the source. */
export const ExtractionCreateSchema = z
    .object({
    sourceKind: ExtractionSourceKindSchema,
    /** Source: a persisted chat session id ("chat"), or raw text ("text"/"document"). */
    sessionId: z.string().min(1).nullable().default(null),
    text: z.string().min(1).max(200_000).nullable().default(null),
    /** Optional map to add accepted new nodes to on apply. */
    mapId: z.string().min(1).nullable().default(null),
    /**
     * Ingestion format for `rawSource`. When present together with `rawSource`,
     * the adapter normalises the raw upload to plain text before extraction.
     * Vault doc ingestion does NOT use this path — it passes `extractedText`
     * via `text` directly (already plain text).
     */
    format: z.enum(["markdown", "claude-ai-json", "claude-code-jsonl"]).optional(),
    /**
     * Raw upload content (conversation export, .jsonl file, markdown doc).
     * Normalised by the ingestion adapter selected via `format`.
     * Max 2 MB covers the largest expected conversation exports.
     */
    rawSource: z.string().min(1).max(2_000_000).optional(),
})
    .refine((v) => {
    const sources = [Boolean(v.sessionId), Boolean(v.text), Boolean(v.rawSource)];
    return sources.filter(Boolean).length === 1;
}, {
    message: "Provide exactly one of sessionId, text, or rawSource",
})
    .refine((d) => !d.rawSource || !!d.format, {
    path: ["format"],
    message: "format is required when rawSource is provided",
});
/** Review one item: accept/reject, optionally editing the payload first. */
export const ExtractionItemReviewSchema = z.object({
    status: z.enum(["accepted", "rejected", "pending"]),
    payload: z.record(z.string(), z.unknown()).optional(),
});
export const ExtractionItemParamSchema = z.object({
    id: z.string().uuid(),
    itemId: z.string().uuid(),
});
export const ExtractionListResponseSchema = z.object({
    extractions: z.array(ExtractionViewSchema),
});
/** Query extractions, optionally filtered to one chat session and/or map. */
export const ExtractionListQuerySchema = z.object({
    sessionId: z.string().min(1).nullable().default(null),
    mapId: z.string().min(1).nullable().default(null),
    /** When true, only return extractions still open for review (or still processing). */
    openOnly: z
        .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
        .optional()
        .default(false),
});
// ─── Canonical graph mutation ledger ───────────────────────────────────────
//
// The authored graph lives in Postgres, and every meaningful graph mutation is
// also appended to this ledger. This is the rebuild/undo/learning substrate for
// the pasted 2026-06-06 personal ideation spec: proposal precision, selection
// learning, snapshots, and any later graph-DB projection should consume this
// log rather than inferring history from current node/edge state.
export const IDEATION_MUTATION_OPS = [
    "node_add",
    "node_edit",
    "node_del",
    "edge_add",
    "edge_edit",
    "edge_del",
    "map_add",
    "map_edit",
    "map_archive",
    "map_membership_add",
    "map_membership_del",
    "map_member_display",
    "edge_archive",
    "extraction_item_accept",
    "extraction_item_reject",
    "extraction_item_reset",
    "extraction_apply",
    "extraction_discard",
];
export const IdeationMutationOpSchema = z.enum(IDEATION_MUTATION_OPS);
export const IDEATION_MUTATION_TARGET_KINDS = [
    "node",
    "edge",
    "map",
    "membership",
    "extraction",
    "extraction_item",
];
export const IdeationMutationTargetKindSchema = z.enum(IDEATION_MUTATION_TARGET_KINDS);
// `ai`      — AI-proposed, human-accepted through the reviewed diff.
// `ai-auto` — AI-proposed AND autonomously applied by hybrid curation (ADR-0075).
//             Distinct from `ai` so the review-after feed can isolate (and revert)
//             exactly the writes no human gated.
export const IDEATION_MUTATION_ACTORS = ["human", "ai", "ai-auto", "system"];
export const IdeationMutationActorSchema = z.enum(IDEATION_MUTATION_ACTORS);
export const IdeationMutationSchema = z.object({
    id: z.string().uuid(),
    /** Extraction/proposal run id, snapshot run id, or null for direct manual edits. */
    runId: z.string().uuid().nullable().default(null),
    op: IdeationMutationOpSchema,
    targetKind: IdeationMutationTargetKindSchema,
    targetId: z.string().uuid().nullable().default(null),
    before: z.record(z.string(), z.unknown()).nullable().default(null),
    after: z.record(z.string(), z.unknown()).nullable().default(null),
    actor: IdeationMutationActorSchema,
    createdBy: z.string().max(320),
    createdAt: z.string(),
});
export const IdeationMutationListQuerySchema = z.object({
    targetId: z.string().uuid().nullable().default(null),
    runId: z.string().uuid().nullable().default(null),
    /** Filter to one actor — drives the "review-after" feed (`actor=ai-auto`). */
    actor: IdeationMutationActorSchema.nullable().default(null),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const IdeationMutationListResponseSchema = z.object({
    mutations: z.array(IdeationMutationSchema),
});
// ─── Node → Document attachment ─────────────────────────────────────────────
//
// A node may have one or more vault documents attached (e.g. a PDF patent, an
// uploaded transcript, a reference spec). These schemas cover the three API
// surfaces: attach (POST body), route params (DELETE), and the JOIN view row
// (GET list response).
/** POST body: attach a vault document to a node. */
export const NodeAttachDocumentSchema = z.object({
    documentId: z.string().uuid(),
});
/** Route params for node-scoped document operations (e.g. DELETE /nodes/:id/documents/:documentId). */
export const NodeDocumentParamSchema = z.object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
});
/** A single row from the node-documents JOIN view (response — kept loose). */
export const NodeDocumentViewSchema = z.object({
    documentId: z.string().uuid(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    /**
     * Loose string (not a narrow enum) so an unknown or legacy extraction status
     * value never breaks the list view. Known values: pending | processing |
     * complete | failed. (Response-schema / loose-read principle.)
     */
    extractionStatus: z.string(),
    /**
     * Phase 9.3: complete extract with non-empty text. False for pending,
     * failed, or complete+empty (scans). Do not infer this from status alone.
     */
    extractUsable: z.boolean(),
});
// ─── Theme decomposition (map hierarchy Phase 1) ────────────────────────────
/** One proposed theme = a future child map + the existing node ids it groups. */
export const ThemeProposalItemSchema = z.object({
    /** Client-stable id for merge/split/rename in the review panel (not persisted). */
    tempId: z.string().min(1).max(60),
    /** Human-readable theme name → becomes the child map title. */
    name: z.string().min(1).max(200),
    /** Optional one-line "why these group" note (advisory; not persisted). */
    rationale: z.string().max(600).default(""),
    /** Existing node ids assigned to this theme. */
    nodeIds: z.array(z.string().min(1)).default([]),
});
/** The full grouping proposal for a map. Loose (AI-produced + client-edited). */
export const ThemeProposalSchema = z.object({
    themes: z.array(ThemeProposalItemSchema).default([]),
    /** Node ids the engine could not place — stay as direct hub members on apply. */
    unassigned: z.array(z.string().min(1)).default([]),
});
/** Request body for POST /maps/:id/decompose — the (possibly edited) proposal. */
export const ThemeDecomposeRequestSchema = z.object({
    proposal: ThemeProposalSchema,
});
/** Loose response summary for decompose / flatten. */
export const HubDecomposeResultSchema = z.object({
    hubId: z.string().min(1),
    kind: z.string(),
    childMapIds: z.array(z.string()).default([]),
    hubMemberCount: z.number().int().default(0),
});
/** Request body for PATCH /nodes/:id/membership — move a node between child maps. */
export const NodeMembershipMoveSchema = z.object({
    fromMapId: z.string().min(1),
    toMapId: z.string().min(1),
});
// ─── Peer map links (ADR-0113) ──────────────────────────────────────────────
export const IdeationMapLinkSchema = z.object({
    id: z.string().uuid(),
    fromMapId: z.string().uuid(),
    toMapId: z.string().uuid(),
    rel: z.string().min(1).max(60),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** Populated on list when available — target map title for Atlas UI. */
    toMapTitle: z.string().optional(),
    fromMapTitle: z.string().optional(),
    direction: z.enum(["out", "in"]).optional(),
});
export const IdeationMapLinkCreateSchema = z.object({
    toMapId: z.string().uuid(),
    rel: z.string().min(1).max(60).default("related"),
});
export const IdeationMapLinkListQuerySchema = z.object({
    inbound: z
        .string()
        .optional()
        .transform((v) => v === "true" || v === "1"),
});
export const IdeationMapLinkListResponseSchema = z.object({
    links: z.array(IdeationMapLinkSchema),
});
/** CRM convenience: peer maps that also have a project overlay. */
export const RelatedProjectSchema = z.object({
    projectId: z.string().uuid(),
    name: z.string(),
    mapId: z.string().uuid(),
    rel: z.string(),
    direction: z.enum(["out", "in"]),
});
export const RelatedProjectListResponseSchema = z.object({
    related: z.array(RelatedProjectSchema),
});
//# sourceMappingURL=ideation-maps.js.map